import React from 'react';
import classNames from 'classnames';
import { useLocation } from 'react-router-dom';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { useConfiguration } from '../../context/configurationContext';

import {
  ExternalLink,
  IconSocialMediaFacebook,
  IconSocialMediaLinkedIn,
  LinkedLogo,
  NamedLink,
} from '../../components';

import { isMobileBottomNavHidden } from '../TopbarContainer/Topbar/navigationConfig';

import {
  aboutColumn,
  categoryLinkProps,
  helpColumn,
  howItWorksColumn,
  legalItems,
  paymentBadges,
  popularColumn,
  resolveLinkProps,
  socialLinks,
  trustColumn,
} from './footerConfig';

import css from './FooterContainer.module.css';

// A category link reads as the category does in the menu (the "&" switch in
// configCategories.js included); its message is the fallback.
const categoryName = (categories, path = []) =>
  path.reduce((found, id, i) => {
    const list = i === 0 ? categories : found?.subcategories;
    return (list || []).find(c => c.id === id) || null;
  }, null)?.name;

const LinkColumn = ({ column, intl, categories, className }) => (
  <div className={classNames(css.linkColumn, className)}>
    <h4 className={css.linkColumnTitle}>{intl.formatMessage({ id: column.titleId })}</h4>
    <ul className={css.linkList}>
      {column.links.map(link => {
        const label =
          (link.categoryPath && categoryName(categories, link.categoryPath)) ||
          intl.formatMessage({ id: link.labelMessageId });
        return (
          <li key={link.key} className={css.linkItem}>
            {link.href ? (
              <ExternalLink className={css.link} href={link.href}>
                {label}
              </ExternalLink>
            ) : (
              <NamedLink
                className={css.link}
                {...(link.categoryPath ? categoryLinkProps(link) : resolveLinkProps(link))}
              >
                {label}
              </NamedLink>
            )}
          </li>
        );
      })}
    </ul>
  </div>
);

/**
 * Bottom-bar legal row: a single inline string of
 * `Personvern · Cookies · Org.nr 935 435 102 · © 2026 Zuprize`. The
 * exact wording — including the middot separators — is owned by the
 * `Footer.legalRow` translation so future locales can rearrange or
 * re-punctuate the row freely. Each `{slot}` resolves to either a
 * NamedLink or static text.
 */
const LegalRow = ({ intl }) => {
  const currentYear = new Date().getFullYear();
  const linkFor = item => (
    <NamedLink className={css.legalLink} {...resolveLinkProps(item)}>
      {intl.formatMessage({ id: item.labelMessageId })}
    </NamedLink>
  );
  const textFor = item =>
    intl.formatMessage(
      { id: item.labelMessageId },
      item.key === 'copyright' ? { year: currentYear } : undefined
    );
  const byKey = Object.fromEntries(legalItems.map(item => [item.key, item]));

  return (
    <p className={css.legalRow}>
      <FormattedMessage
        id="Footer.legalRow"
        values={{
          personvern: linkFor(byKey.personvern),
          cookies: linkFor(byKey.cookies),
          orgNumber: textFor(byKey.org),
          copyright: textFor(byKey.copyright),
        }}
      />
    </p>
  );
};

/**
 * Site-wide custom footer. Built from the May-2026 footer spec:
 *   - 5 desktop columns (brand + 4 link columns)
 *   - dedicated payment-icon row
 *   - bottom bar: legal items left, social labels right
 *   - white bg, black text, 1px #DDDDDD dividers, 64/40px padding
 *
 * Content lives in `footerConfig.js`.
 *
 * @param {Object} props
 * @param {string} [props.className] - e.g. the campaign page's off-white footer
 * @returns {JSX.Element}
 */
const FooterContainer = props => {
  const { className } = props;
  const intl = useIntl();
  const config = useConfiguration();
  const location = useLocation();

  // Pages without MobileBottomNav don't need the extra mobile bottom-
  // padding that normally reserves room for the fixed bottom nav.
  const noBottomNav = isMobileBottomNavHidden(location?.pathname);

  // The listing detail page (/l/:slug/:id, plus its variant suffix)
  // docks the BuyNowButton as a fixed bottom bar on mobile, so the
  // footer needs extra bottom clearance there to not sit under it.
  const isListingPage = /^\/l\/[^/]+\/[^/]+/.test(location?.pathname || '');

  return (
    <footer className={classNames(css.root, className)}>
      <div
        className={classNames(css.inner, {
          [css.innerNoBottomNav]: noBottomNav,
          [css.innerListingPage]: isListingPage,
        })}
      >
        <div className={css.columns}>
          <div className={css.brandColumn}>
            <LinkedLogo
              rootClassName={css.brandLogoLink}
              logoClassName={css.brandLogo}
              logoImageClassName={css.brandLogoImage}
              alt={intl.formatMessage({ id: 'Footer.brandWordmark' })}
            />
            <p className={css.brandTagline}>
              <FormattedMessage id="Footer.brandTagline" />
            </p>
          </div>
          {/* First on desktop; on phones below the other sections (Guro, 14 October). */}
          <LinkColumn
            column={popularColumn}
            intl={intl}
            categories={config?.categoryConfiguration?.categories}
            className={css.popularColumn}
          />
          <LinkColumn column={aboutColumn} intl={intl} />
          <LinkColumn column={howItWorksColumn} intl={intl} />
          <LinkColumn column={trustColumn} intl={intl} />
          <LinkColumn column={helpColumn} intl={intl} />
        </div>

        <div
          className={css.paymentRow}
          role="list"
          aria-label={intl.formatMessage({ id: 'Footer.paymentMethodsAriaLabel' })}
        >
          <div className={css.paymentBadgesLeft}>
            {paymentBadges
              .filter(b => b.position !== 'right')
              .map(badge => (
                <span key={badge.key} className={css.paymentBadge} role="listitem">
                  <img
                    src={badge.src}
                    alt={badge.alt}
                    className={css.paymentImage}
                    loading="lazy"
                  />
                </span>
              ))}
          </div>
          {paymentBadges.some(b => b.position === 'right') ? (
            <div className={css.paymentBadgesRight}>
              {paymentBadges
                .filter(b => b.position === 'right')
                .map(badge => (
                  <span key={badge.key} className={css.paymentBadge} role="listitem">
                    <img
                      src={badge.src}
                      alt={badge.alt}
                      className={css.paymentImage}
                      loading="lazy"
                    />
                  </span>
                ))}
            </div>
          ) : null}
        </div>

        <div className={css.bottomBar}>
          <LegalRow intl={intl} />
          <div className={css.socialRow}>
            {socialLinks.map(item => {
              const Icon =
                item.key === 'facebook'
                  ? IconSocialMediaFacebook
                  : item.key === 'linkedin'
                  ? IconSocialMediaLinkedIn
                  : null;
              const label = intl.formatMessage({ id: item.labelMessageId });
              return (
                <ExternalLink
                  key={item.key}
                  href={item.href}
                  className={css.socialLink}
                  title={label}
                  aria-label={label}
                >
                  {Icon ? <Icon className={css.socialIcon} /> : label}
                </ExternalLink>
              );
            })}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default FooterContainer;
